import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { UserAnalyticsPanel } from '../components/AccountAnalytics';

function formatDisplayDate(value) {
  if (!value) return 'Never';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'Never';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTier, setFilterTier] = useState('all');
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await api.get('/users/analytics');
      setAnalytics(res.data);
    } catch (err) {
      console.error('Failed to fetch user analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const openLeaderboardRow = (row) => {
    if (row?.id) viewUserDetails(row.id);
  };

  const viewUserDetails = async (userId) => {
    try {
      const response = await api.get(`/users/${userId}`);
      setSelectedUser(response.data);
      setShowModal(true);
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      alert('Failed to load user details');
    }
  };

  const updateUserTier = async (userId, newTier) => {
    try {
      await api.put(`/users/${userId}`, {
        membershipTier: newTier,
        durationDays: newTier === 'premium' ? 30 : undefined,
      });
      alert('User tier updated successfully');
      fetchUsers();
      if (selectedUser && selectedUser._id === userId) {
        viewUserDetails(userId);
      }
    } catch (error) {
      console.error('Failed to update user tier:', error);
      alert(error.response?.data?.message || error.response?.data?.error || 'Failed to update user tier');
    }
  };

  const formatSubscription = (entity) => {
    const tier = entity.membershipTier === 'enterprise' ? 'premium' : entity.membershipTier;
    if (tier !== 'premium') return '—';
    const start = entity.membershipStartDate
      ? new Date(entity.membershipStartDate).toLocaleDateString()
      : '—';
    const expiry = entity.membershipExpiryDate
      ? new Date(entity.membershipExpiryDate).toLocaleDateString()
      : '—';
    return `${start} → ${expiry}`;
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      await api.put(`/users/${userId}`, { isActive: !currentStatus });
      alert(`User ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchUsers();
      if (selectedUser && selectedUser._id === userId) {
        viewUserDetails(userId);
      }
    } catch (error) {
      console.error('Failed to update user status:', error);
      alert('Failed to update user status');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.telegramId?.toString().includes(searchTerm);
    const userTier = user.membershipTier === 'free' ? 'starter' : (user.membershipTier === 'enterprise' ? 'premium' : user.membershipTier);
    const matchesTier = filterTier === 'all' || userTier === filterTier;
    return matchesSearch && matchesTier;
  });

  const getTierBadge = (tier) => {
    const t = tier === 'enterprise' ? 'premium' : (tier === 'free' ? 'starter' : tier);
    const badges = {
      starter: 'text-gray-400 dark:text-gray-500',
      alpha: 'text-amber-600/80 dark:text-amber-400/80',
      premium: 'text-blue-600/80 dark:text-blue-400/80',
    };
    return badges[t] || badges.starter;
  };

  const formatTierLabel = (tier) => {
    if (!tier || tier === 'free' || tier === 'starter') return 'Starter';
    if (tier === 'enterprise' || tier === 'premium') return 'Pro';
    if (tier === 'alpha') return 'α';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  const isPaidTier = (tier) => {
    const t = tier === 'enterprise' ? 'premium' : (tier === 'free' ? 'starter' : tier);
    return t === 'premium' || t === 'alpha';
  };

  const downgradeToStarter = async (userId) => {
    if (!window.confirm('Downgrade this user to Starter? Premium/Alpha access will be removed immediately.')) return;
    await updateUserTier(userId, 'starter');
  };

  const upgradeToAlpha = async (userId) => {
    if (!window.confirm('Upgrade this user to Alpha? They will have free membership forever with unlimited access.')) return;
    try {
      await api.put(`/users/${userId}`, { membershipTier: 'alpha' });
      alert('User upgraded to Alpha successfully');
      fetchUsers();
      if (selectedUser && selectedUser._id === userId) {
        viewUserDetails(userId);
      }
    } catch (error) {
      console.error('Failed to upgrade user to Alpha:', error);
      alert('Failed to upgrade user to Alpha');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-xl text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Users</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Leaderboards, premium/alpha stats, and activity
          </p>
        </div>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Total: {users.length} users
        </div>
      </div>

      <UserAnalyticsPanel
        analytics={analytics}
        loading={analyticsLoading}
        onSelectRow={openLeaderboardRow}
      />

      {/* Filters */}
      <div className="mb-6 flex gap-4">
        <input
          type="text"
          placeholder="Search by username or Telegram ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select
          value={filterTier}
          onChange={(e) => setFilterTier(e.target.value)}
          className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Tiers</option>
          <option value="starter">Starter</option>
          <option value="alpha">Alpha</option>
          <option value="premium">Premium</option>
        </select>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Telegram ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Tier
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Subscription
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredUsers.map((user) => (
              <tr
                key={user._id}
                className="hover:bg-gray-50 dark:bg-gray-800/50 cursor-pointer"
                onClick={() => viewUserDetails(user._id)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                      {user.username?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {user.username || 'Unknown'}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {user.firstName} {user.lastName}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {user.telegramId}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`text-[11px] font-medium ${getTierBadge(user.membershipTier)}`}>
                    {formatTierLabel(user.membershipTier)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                  {formatSubscription(user)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    user.isActive ? 'bg-green-100 text-green-800 dark:text-green-300' : 'bg-red-100 text-red-800'
                  }`}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      viewUserDetails(user._id);
                    }}
                    className="text-blue-600 hover:text-blue-900 mr-3"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* User Detail Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Details</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                {/* Basic Info */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold mb-2">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Username</p>
                      <p className="font-medium">{selectedUser.username || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Telegram ID</p>
                      <p className="font-medium">{selectedUser.telegramId}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">First Name</p>
                      <p className="font-medium">{selectedUser.firstName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Last Name</p>
                      <p className="font-medium">{selectedUser.lastName || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Joined</p>
                      <p className="font-medium">{new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Last Active</p>
                      <p className="font-medium">{formatDisplayDate(selectedUser.lastActiveAt || selectedUser.lastCommandAt || selectedUser.updatedAt)}</p>
                    </div>
                  </div>
                </div>

                {/* Membership */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold mb-2">Membership</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Current Tier</p>
                      <span className={`text-sm font-medium ${getTierBadge(selectedUser.membershipTier)}`}>
                        {(!selectedUser.membershipTier || selectedUser.membershipTier === 'free' || selectedUser.membershipTier === 'starter')
                          ? 'Starter'
                          : selectedUser.membershipTier === 'alpha'
                            ? 'Alpha'
                            : 'Premium'}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Subscription</p>
                      <p className="font-medium">{formatSubscription(selectedUser)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                      <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${
                        selectedUser.isActive ? 'bg-green-100 text-green-800 dark:text-green-300' : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedUser.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Usage Stats */}
                <div className="border-b pb-4">
                  <h3 className="text-lg font-semibold mb-2">Usage Statistics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Tokens Tracked</p>
                      <p className="font-medium">{selectedUser.stats?.tokensTracked || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Alerts Sent</p>
                      <p className="font-medium">{selectedUser.stats?.alertsSent || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Groups Owned</p>
                      <p className="font-medium">{selectedUser.stats?.groupsOwned || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="pt-4">
                  <h3 className="text-lg font-semibold mb-3">Actions</h3>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => upgradeToAlpha(selectedUser._id)}
                      className="px-4 py-2 bg-amber-600 text-white rounded hover:bg-amber-700"
                    >
                      Upgrade to Alpha
                    </button>
                    <button
                      onClick={() => updateUserTier(selectedUser._id, 'premium')}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Upgrade to Premium (30 days)
                    </button>
                    {isPaidTier(selectedUser.membershipTier) && (
                      <button
                        onClick={() => downgradeToStarter(selectedUser._id)}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                      >
                        Downgrade to Starter
                      </button>
                    )}
                    <button
                      onClick={() => toggleUserStatus(selectedUser._id, selectedUser.isActive)}
                      className={`px-4 py-2 rounded ${
                        selectedUser.isActive
                          ? 'bg-red-600 hover:bg-red-700'
                          : 'bg-green-600 hover:bg-green-700'
                      } text-white`}
                    >
                      {selectedUser.isActive ? 'Deactivate' : 'Activate'} User
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
